<template>
  <div :key="componentKey" id="waiting-room" class="flex h-full">

    <div class="w-36 h-full queue">
      <div class="inner px-2 w-36 mt-10">

        <SearchInput />
        <div class="scroll-area-inside">
          <div class="w-full text-sm border-b border-black font-bold mb-2 sticky">
            Arrived
          </div>
          <div v-if="interactWithBoard">
            <div
                v-for="(item, i) in filteredQueue"
                :key="item.uuid"
                draggable="true"
                :data-uuid="item.uuid"
                @dragstart="onNativeDragStart($event, item)"
                @dragend="onNativeDragEnd"
            >
              <QueueCard
                  :id="`queue-${i}`"
                  class="mb-2"
                  :arrival-time="$dayjs.min(
                    $dayjs(item.arrived_at || undefined),
                    $dayjs(item.checkedin_at || undefined)
                  ).toDate()"
                  :appointment="item"
                  :appointment-time="item.starts_at"
                  :doctor-initials="$filters.initials(getDoctorName(item))"
                  :image-url="item.patient.avatar"
                  :name="item.patient.name"
                  :new-patient="item.patient.is_new"
                  :treatments="item.treatments"
                  :uuid="item.uuid"
                  @click="openAppointmentDialog(item)"
              />
            </div>
          </div>

          <div v-if="!interactWithBoard">
            <QueueCard
                :id="`queue-${i}`"
                class="mb-2"
                v-for="(item, i) in filteredQueue"
                :arrival-time="$dayjs.min(
              $dayjs(item.arrived_at || undefined),
              $dayjs(item.checkedin_at || undefined)
            ).toDate()"
                :appointment="item"
                :appointment-time="item.starts_at"
                :doctor-initials="$filters.initials(getDoctorName(item))"
                :image-url="item.patient.avatar"
                :key="`queue-${item.uuid}`"
                :name="item.patient.name"
                :new-patient="item.patient.is_new"
                :treatments="item.treatments"
                :uuid="item.uuid"
            />
          </div>

          <div class="w-full text-sm border-b border-black font-bold mb-2 mt-8 sticky">
            Upcoming

            <div class="addAppointmentWrapper">
                <div class="flex items-center">
                    <v-icon :size="16" @click="openCreateAppointmentModal">mdi-plus</v-icon>
                </div>
            </div>
          </div>
          <QueueCard
              v-for="(item, i) in queueUpcoming"
              :key="`queue-${item.uuid}`"
              :id="`queue-${i}`"
              :uuid="item.uuid"
              class="mb-2"
              :appointment="item"
              :name="item.patient.name"
              :image-url="item.patient.avatar"
              :doctor-initials="$filters.initials(getDoctorName(item))"
              :appointment-time="item.starts_at"
              :new-patient="item.patient.is_new"
              :treatments="item.treatments"
              allowMakeManual
              @click="openAppointmentDialog(item)"
          />
          <div class="w-full text-sm border-b border-black font-bold mb-2 mt-8 sticky">
            Tomorrow
          </div>
          <QueueCard
              v-for="(item, i) in queueTomorrow"
              :key="`queue-${item.uuid}`"
              :id="`queue-${i}`"
              :uuid="item.uuid"
              class="mb-2"
              :appointment="item"
              :name="item.patient.name"
              :image-url="item.patient.avatar"
              :doctor-initials="$filters.initials(getDoctorName(item))"
              :appointment-time="item.starts_at"
              :new-patient="item.patient.is_new"
              :treatments="item.treatments"
          />
        </div>
      </div>
    </div>
    <div class="relative flex flex-col justify-start items-start">
      <draggable
          v-if="interactWithBoard"
          :class="{ inert: isDraggingQueuePatient }"
          :drag-class="dragClass"
          :group="group"
          :list="filteredRooms"
          :move="onRoomPatientMoved"
          draggable=".ableToDragAndDrop"
          @end="onRoomPatientDragEnd"
          @start="onRoomPatientDragStart"
      >
        <div
          v-for="(room, i) in rooms"
          :key="`room-header-${i + 1}`"
          :id="`room-${i}`"
          :class="{ 'last-room-height': i === rooms.length - 1,
          'ableToDragAndDrop': ableToDragAndDrop(room) }"
          class="block block--header"
          style="height: 68px;"
        >
          <div :id="`roomborder-${i}`"></div>
          <div
            class="block--header__text truncate cursor-pointer"
            :style="{
              backgroundColor: room.color,
              color: $filters.foregroundColor(room.color)
            }"
          >
            {{ room.name.toUpperCase() }}
          </div>
          <div
            v-if="!!activeAppointment(room) && filteredAppointments.find(a => a === room.uuid)"
            class="flex flex-col p-1 w-full cursor-pointer"
            @mousedown="start(room)"
            @touchstart="start(room)"
            @mouseleave="stop(room)"
            @mouseup="stop(room)"
            @touchcancel="stop(room)"
            @touchend="stop(room)"
            @click.stop="$store.dispatch('selectAppointment', { appointment: activeAppointment(room) })"
          >
            <Avatar
              size="24"
              :src="activeAppointment(room).patient.avatar"
              :alt="activeAppointment(room).patient.name"
            />
            <div class="text-xs text-gray-darker truncate select-none">
              {{ patientName(activeAppointment(room).patient.name) }}
            </div>
            <div class="flagsWrapper">
              <div>
                <v-icon
                    v-if="activeAppointment(room).has_flags"
                    color="red-darken-2"
                    :size="18">mdi-flag</v-icon>
              </div>
              <div>
                <v-icon
                    v-if="activeAppointment(room).has_traction_flags"
                    color="green-darken-2"
                    :size="18">mdi-flag</v-icon>
              </div>
            </div>
          </div>
        </div>
      </draggable>
      <div
          v-for="(room, i) in rooms"
          v-if="!interactWithBoard"
          :key="`room-header-${i + 1}`"
          :id="`room-${i}`"
          :class="{ 'last-room-height': i === rooms.length - 1 }"
          class="block block--header"
          style="height: 68px;"
      >
        <div :id="`roomborder-${i}`"></div>
        <div
            class="block--header__text truncate"
            :style="{
              backgroundColor: room.color,
              color: $filters.foregroundColor(room.color)
            }"
        >
          {{ room.name.toUpperCase() }}
        </div>
        <div
            v-if="!!activeAppointment(room) && filteredAppointments.find(a => a === room.uuid)"
            class="flex flex-col p-1 w-full cursor-pointer"
            :class="!!filteredAppointments.find(a => a === room.uuid) ? 'filtered': null"
            @click.stop="$store.dispatch('selectAppointment', { appointment: activeAppointment(room) })"
        >
          <Avatar
              size="24"
              :src="activeAppointment(room).patient.avatar"
              :alt="activeAppointment(room).patient.name"
          />
          <div class="text-xs text-gray-darker truncate">
            {{ patientName(activeAppointment(room).patient.name) }}
          </div>
        </div>
      </div>
    </div>
    <div
      id="room-slots"
      class="room-slots__container"
      :class="{
        [isDraggingQueuePatientGhostClass]: isDraggingQueuePatient,
        'room-slots--active': isDraggingQueuePatient,
        'room-slots--inactive': !isDraggingQueuePatient
      }"
    >
      <div
        v-for="(slot, index) in roomSlots"
        :key="slot.uuid"
        class="room-slots__slot"
        :data-room-index="index"
        @dragover.prevent="onSlotDragOver($event, index)"
        @dragleave="onSlotDragLeave($event, index)"
        @drop.prevent="onSlotDrop($event, index)"
      ></div>
    </div>
    <div
      v-if="isDraggingQueuePatient"
      class="room-slots__cancel"
    ></div>
    <div
      v-if="dragWarning"
      class="room-drag-warning"
      :style="{ top: `${dragWarning.index * 68}px` }"
    >
      <span class="room-drag-warning__label">
        <v-icon color="#3d2e00" size="18" class="mr-1">mdi-alert</v-icon>
        {{ dragWarning.text }}
      </span>
    </div>
  </div>
</template>

<script lang="ts">
import sum from 'hash-sum';
import { defineComponent } from 'vue';
import {mapActions, mapState} from 'vuex';
import { isEqual } from 'lodash-es';
import { VDraggable as draggable } from '@/ui';
import Avatar from '@/components/basic/Avatar.vue';
import QueueCard from '@/components/cards/QueueCard.vue';
import SearchInput from '@/components/basic/SearchInput.vue';

export default defineComponent({
  name: 'WaitingRoom',
  components: { Avatar, draggable, QueueCard, SearchInput },
  props: {
    queue: {
      type: Array,
      required: true,
    },
    queueUpcoming: {
      type: Array,
      required: true,
    },
    queueTomorrow: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      componentKey: 0,
      dragClass: 'ghost',
      isDraggingQueuePatient: false,
      isDraggingRoomPatient: false,
      isDraggingRoomPatientIndex: 0,
      filteredAppointments: [],
      interval: undefined,
      count: 0,
      group: {
        name: 'foo',
        pull: true,
        put(to, from) {
          console.log({ to, from });
          // Return true to allow, return false to disallow
          return true;
        },
      },
      rooms: [],
      roomSlots:
        Array(20).fill(null).map(() => ({ uuid: sum(Math.random()) })) as any,
      roomSlotsDragIndexIsValid: false,
      interactWithBoard: false,
      draggedAppointment: null,
      dragWarning: null,
    };
  },
  async mounted() {
    this.interactWithBoard = await this.$store.dispatch('ableUserTo', 'interact with board');
    this.setRooms();
  },
  computed: {
    ...mapState({
      roomsInStore: (state: any) => state.auth.dashboard.rooms,
      choosenAppt: (state: any) => state.appointment,
      time: (state: any) => state.time,
      settingsRooms: (state: any) => state.rooms ?? [],
    }),
    filter() {
      return this.$store.state.filter;
    },
    filteredQueue() {
      if (!this.filter) { return this.queue; }
      return this.queue.filter((item) => {
        const doctors = item.treatments.map((t) => t.doctor.uuid);
        return doctors.includes(this.filter);
      });
    },
    filteredRooms() {
      this.filterRooms();
      return [...this.rooms];
    },
    filteredIds() {
      return this.filteredRooms.map((r) => r.name);
    },
    ids() {
      return this.rooms.map((r) => r.name);
    },
    isDraggingQueuePatientGhostClass() {
      return `room-slots__slot--${
        this.roomSlotsDragIndexIsValid ? '' : 'in'
      }valid`;
    },
  },
  watch: {
    roomsInStore: {
      deep: true,
      handler() {
        this.setRooms();
      },
    },
    rooms() { this.resetRoomSlots(); },
  },
  methods: {
    ...mapActions(['selectAppointment']),
    openCreateAppointmentModal() {
      this.$store.dispatch('TOGGLE_CREATE_APPOINTMENT_MODAL');
    },
    ableToDragAndDrop(room) {
      return !this.activeAppointment(room)
          || !this.activeAppointment(room).treatments.some((x) =>
              !!x.started_at && (x.status === 'processing'),
          );
    },
    activeAppointment(room: any) {
      return room?.appointments?.find(({ status: x }) =>
        ['processing', 'paused'].includes(x),
      ) ?? false;
    },
    isTreatmentsOrdered(appointment) {
      let hasOrderVal = false;

      if (appointment?.treatments.length > 0) {
        appointment?.treatments.map(({order_val: orderVal}) => hasOrderVal = !!orderVal );
      }
      return hasOrderVal;
    },
    filterRooms() {
      const filtered = (t) => {
        if (t.doctor) {
          if (t.doctor.uuid === this.filter) {
            return true;
          }
        }
        return false;
      };

      const filteredAppointments = [];
      this.rooms.forEach((r) => {
        const appt = this.activeAppointment(r);
        if (appt) {
          filteredAppointments.push(r.uuid);
        }
      });
      this.filteredAppointments = filteredAppointments;
    },
    getDoctorName(item) {
      return item.treatments?.[0]?.doctor?.name ?? item.doctor?.name ?? 'X X';
    },
    // Native HTML5 drag and drop handlers
    onNativeDragStart(evt: DragEvent, appointment: any) {
      console.log('Native drag started:', appointment.uuid);
      this.isDraggingQueuePatient = true;
      this.draggedAppointment = appointment;
      if (evt.dataTransfer) {
        evt.dataTransfer.effectAllowed = 'copy';
        evt.dataTransfer.setData('text/plain', appointment.uuid);
        evt.dataTransfer.setData('application/json', JSON.stringify(appointment));
      }
    },
    onNativeDragEnd() {
      console.log('Native drag ended');
      this.isDraggingQueuePatient = false;
      this.draggedAppointment = null;
      this.dragWarning = null;
      // Reset any visual indicators
      document.querySelectorAll('.room-slots__slot--dragover').forEach(el => {
        el.classList.remove('room-slots__slot--dragover');
      });
    },
    onSlotDragOver(evt: DragEvent, roomIndex: number) {
      const target = evt.currentTarget as HTMLElement;
      const isValidDrop = !this.activeAppointment(this.rooms[roomIndex]);
      this.setDragWarning(this.draggedAppointment, roomIndex);
      if (isValidDrop) {
        target?.classList.toggle('room-slots__slot--dragover', !this.dragWarning);
        this.roomSlotsDragIndexIsValid = true;
      } else {
        this.roomSlotsDragIndexIsValid = false;
      }
    },
    onSlotDragLeave(evt: DragEvent, roomIndex: number) {
      const target = evt.currentTarget as HTMLElement;
      target?.classList.remove('room-slots__slot--dragover');
      if (this.dragWarning?.index === roomIndex) { this.dragWarning = null; }
    },
    async onSlotDrop(evt: DragEvent, roomIndex: number) {
      console.log('Slot drop event:', { roomIndex, evt });
      const target = evt.currentTarget as HTMLElement;
      target?.classList.remove('room-slots__slot--dragover');

      const appointmentUuid = evt.dataTransfer?.getData('text/plain');
      console.log('Dropped appointment UUID:', appointmentUuid);

      if (!appointmentUuid) {
        console.warn('No appointment UUID found in drop data');
        return;
      }

      const isValidDrop = !this.activeAppointment(this.rooms[roomIndex]);
      console.log('Is valid drop:', isValidDrop, 'Room index:', roomIndex);

      if (isValidDrop) {
        await this.startAppointment(appointmentUuid, roomIndex);
      }

      this.isDraggingQueuePatient = false;
    },
    missingTreatments(appointment, room) {
      const roomTreatments = this.settingsRooms.find((r) => r.uuid === room.uuid)?.treatments ?? [];
      // Rooms with no treatments and treatments set up for no room restrict nothing.
      if (!roomTreatments.length) { return []; }
      // Match by name too: appointments can carry another office's treatment rows.
      const listed = (list, { uuid, name }) => list.some((t) =>
        t.uuid === uuid || t.name.toLowerCase() === name.toLowerCase());
      const configured = this.settingsRooms.flatMap((r) => r.treatments ?? []);
      return [...new Set((appointment?.treatments ?? [])
        .filter((t) => !t.ended_at && t.treatment)
        .map((t) => t.treatment)
        .filter((t) => listed(configured, t) && !listed(roomTreatments, t))
        .map((t) => t.name))];
    },
    setDragWarning(appointment, roomIndex) {
      const room = this.rooms[roomIndex];
      const missing = !room || this.activeAppointment(room) ? [] : this.missingTreatments(appointment, room);
      const warning = missing.length
        ? { index: roomIndex, text: `${room.name} does not include ${missing.join(', ')}` }
        : null;
      if (!isEqual(warning, this.dragWarning)) { this.dragWarning = warning; }
    },
    onRoomPatientDragStart() { this.isDraggingRoomPatient = true; },
    getOrder(treatments) {
      const treatmentsUuidAr = [];

      if (treatments.length === 0) { return treatmentsUuidAr; }

      treatments.map((t) => {
        treatmentsUuidAr.push(t.uuid);
      });

      return treatmentsUuidAr;
    },
    async onRoomPatientDragEnd(event) {
      const { newIndex, oldIndex } = event;
      this.isDraggingRoomPatientIndex = this.oldIndex;
      this.isDraggingRoomPatient = false;
      this.dragWarning = null;
      const unchanged = isEqual(this.roomsInStore, this.filteredRooms);
      const disabled = !!this.rooms[newIndex].appointments.find(
        ({ status: x }) => ['processing', 'paused'].includes(x),
      );
      if (unchanged || disabled) { return; }
      let newRoomIndex;
      if (disabled) {
        event.preventDefault();
        newRoomIndex = oldIndex;
      } else {
        newRoomIndex = newIndex;
      }

      const appt = this.roomsInStore[oldIndex].appointments.find(({ status: x }) => ['processing', 'paused'].includes(x));
      const apptUuid = appt.uuid;
      const newRoomUuid = this.roomsInStore[newRoomIndex].uuid;
      const payload = {
        appointmentId: apptUuid,
        params: {
          user: this.$store.state.auth.user.uuid,
          status: appt.status,
          room: newRoomUuid,
          localInfo: {
            roomIndex: newRoomIndex,
            appointmentId: apptUuid,
          },
        },
      };
      const success = await this.$store.dispatch('UPDATE_APPOINTMENT_STATUS', payload);
      if (!success) {
        this.setRooms();
        this.$eventBus.emit('emit-error', { state: true, text: 'Something went wrong. Please try again.' });
      }
    },
    async onRoomPatientMoved(event) {
      if (this.activeAppointment(this.rooms[event.oldIndex])) { return false; }
      this.dragClass = 'ghost';
      const { element, futureIndex, index } = event.draggedContext;
      this.setDragWarning(this.activeAppointment(this.rooms[index]), futureIndex);
      // const disabled = !!this.rooms[futureIndex].appointments.find(({ status: x }) => ['processing', 'paused'].includes(x));
      // if (disabled) {
      //   this.dragClass = 'ghost--red';
      // }
    },
    patientName(name) { return name.replace('Dr. ', ''); },
    resetRoomSlots() {
      if (!!this.rooms && this.rooms.length > 0) {
        this.roomSlots = Array(this.rooms.length).fill(null).map(() => ({
          uuid: sum(Math.random()),
        }));
      } else {
        this.roomSlots = Array(20).fill(null).map(() => ({ uuid: sum(Math.random()) }));
      }
    },
    roomIsOccupied(i) {
      return this.rooms[i]?.appointments?.find(({ status: x }) =>
        ['processing', 'paused'].includes(x),
      ) ?? false;
    },
    setRooms() { this.rooms = [...this.roomsInStore]; },
    async startAppointment(appointmentId, roomIndex) {
      const { dispatch } = this.$store;
      const appt = this.queue.find((appointment) => appointment.uuid === appointmentId);

      if (!!appt && appt.treatments.length > 0 && !this.isTreatmentsOrdered(appt)) {
        const treatmentList = this.getOrder(appt.treatments);
        await dispatch('UPDATE_APPT_TREATMENT_ORDER', {
          appointmentId,
          params: {
            treatmentsUuidAr: treatmentList,
          },
        });
      }

      await dispatch('UPDATE_APPOINTMENT_STATUS', {
        appointmentId,
        params: {
          room: this.rooms[roomIndex].uuid,
          status: 'new',
          user: this.$store.state.auth.user.uuid,
          localInfo: {
            roomIndex,
            appointmentId,
          },
        },
      });
    },
    openAppointmentDialog(appointment) {
      const payload = {
        appointment,
      };
      this.selectAppointment(payload);
    },
    openAppointment(room) {
      this.$store.dispatch('selectAppointment', { appointment: this.activeAppointment(room) });
    },
    start(room): void {
      const canMove = this.ableToDragAndDrop(room);

      if (!this.interval && !canMove) { this.interval = setInterval(() => {

        this.count++;
        if (this.count > 5) {
          this.$eventBus.emit('emit-error', {
            state: true,
            text: 'You can\'t move patient while treatment is running.',
          });
        }
      }, 30);
      }
    },

    stop(room): void {
      clearInterval(this.interval);

      if (this.count > 0 && this.count < 5) {
        this.openAppointment(room);
      }

      this.interval = undefined;
      this.count = 0;
    },
  },
});
</script>

<style scoped>

#waiting-room {
  position: absolute;
  top: 0;
  left: 0;
  width: 264px;
  height: 100%;
  overflow: visible;

  scroll-padding-bottom: 10px;
}

.room-border--default {
  position: absolute;
  top: 0;
  left: -2px;
  z-index: 2;
  width: calc(100vw - 144px - 18px);
  max-width: 860px;
  height: 68px;
  border: 3px solid #a0fcd1;
}

.queue {
  height: 100%;
  background-color: #eaeaea;
  overflow: hidden;
}

.queue > .inner {
  position: fixed;
  top: 0;
  bottom: 0;
  z-index: 7;
  overflow-y: scroll;
  overflow-x: hidden;
  padding-top: 0;
  background-color: #eaeaea;
  scroll-padding-top: 100px;
}

.queue > .inner .sticky {
  top: 0;
  z-index: 5;
  padding-top: 20px;
  background-color: #eaeaea;
  ;
}

.inner .scroll-area-inside {
}

.inner::-webkit-scrollbar {
  display: block;
  width: 5px;
  background-color: #eaeaea;
  border-radius: 5px;
  margin: 0 10px;
}

.inner::-webkit-scrollbar-track {
  border-radius: 8px;
  background-color: #e7e7e7;
  border: 1px solid #cacaca;
}

.inner::-webkit-scrollbar-thumb {
  border-radius: 8px;
  background-color: #4e61b1;
}

.block {
  position: relative;
  flex: none;
  width: 112px;
  height: 100%;
  background-color: transparent;
  border-top: 2px solid white;
  border-left: 2px solid white;
  @apply flex justify-center items-center;
}

.block[disabled] {
  pointer-events: none;
}

.block--header {
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 120px;
  background-color: white;
}

.block--header__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @apply px-1 w-full flex items-center text-xs text-gray-darker font-bold;
}

.last-room-height {
  height: 69.5px !important;
}

.room--green-border {
  position: absolute;
  top: 0;
  left: -2px;
  z-index: 2;
  width: calc(100vw - 144px);
  max-width: 880px;
  height: 68px;
  border: 3px solid #a0fcd1;
}

.room--red-border {
  position: absolute;
  top: 0;
  left: -2px;
  z-index: 2;
  width: calc(100vw - 144px);
  max-width: 880px;
  height: 68px;
  border: 3px solid #e83123;
}

.ghost {
  background: #a0fcd1;
  opacity: .5;
}

.ghost--red {
  background: #e83123;
  opacity: .5;
}

.room-slots__cancel {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  width: 144px;
}

.room-slots__cancel > * {
  height: 100vh;
}

.room-slots__container {
  position: absolute;
  top: 0;
  left: 144px;
  width: calc(100vw - 144px);
  max-width: 880px;
  z-index: 50;
}

.room-slots__container > * {
  z-index: 1000;
  height: 68px;
}

.room-slots--active .room-slots__slot {
  background-color: rgba(160, 252, 209, 0.3);
  border: 1px dashed #a0fcd1;
}

.room-slots__slot--dragover {
  background-color: rgba(160, 252, 209, 0.6) !important;
  border: 3px solid #a0fcd1 !important;
}

.room-drag-warning {
  position: absolute;
  left: 144px;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: calc(100vw - 144px);
  max-width: 880px;
  height: 68px;
  padding-right: 12px;
  background-color: rgba(255, 193, 7, 0.2);
  border: 3px solid #ffc107;
  pointer-events: none;
}

.room-drag-warning__label {
  display: flex;
  align-items: center;
  padding: 4px 10px;
  background-color: #ffc107;
  border-radius: 6px;
  color: #3d2e00;
  font-size: 13px;
  font-weight: bold;
}

.room-slots__container > *.sortable-chosen {
  flex-direction: row;
  align-items: flex-end;
  width: 100% !important;
  max-width: initial;
  margin: 0 !important;
  border-radius: 0;
}

.room-slots__container.room-slots__slot--invalid > .sortable-chosen {
  border: 5px solid var(--v-red-base)
}

.room-slots__container.room-slots__slot--valid > .sortable-chosen {
  border: 5px solid var(--v-green-base)
}

.room-slots__container > *.sortable-chosen :deep(.corner-flag) {
  right: 0.5px;
}

.room-slots__container > *.sortable-chosen :deep(.corner-flag > *) {
  padding-top: 2px;
  border-bottom-left-radius: 6px;
}

.room-slots__container > *.sortable-chosen :deep(.v-avatar) {
  min-width: initial !important;
  width: 24px !important;
  min-height: initial !important;
  height: 24px !important;
}

.room-slots__container > *.sortable-chosen :deep(.timestamps) {
  display: flex;
  align-items: baseline;
  margin-left: 10px;
}

.room-slots__container > *.sortable-chosen :deep(.timestamps > * + *) {
  margin-left: 10px;
}

.inert {
  pointer-events: none
}

.room-slots--active {
  z-index: 100;
  pointer-events: auto !important;
}

.room-slots--inactive {
  pointer-events: none;
  visibility: hidden;
}

.flagsWrapper {
  position: absolute;
  top: 15px;
  right: 5px;

  display: flex;
  justify-content: flex-end;
}

.addAppointmentWrapper {
  position: absolute;
  bottom: 3px;
  right: 5px;
}

.addAppointmentWrapper i {
  color: #111111;
  cursor: pointer;
  transition: .2s ease-in;
}

.addAppointmentWrapper i:hover {
  transform: scale(1.5);
}
</style>
